import {
    createBrowserRouter,
    RouterProvider,
    createRoutesFromElements,
    Route,
    Navigate
} from 'react-router-dom'
import React, { Suspense, lazy } from 'react'
import { SWRConfig } from 'swr'
import './App.css'
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import PrivateRoute from './components/PrivateRoute'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingScreen from './components/Common/LoadingScreen'
import FloatingChatWidget from './components/ChatWidget/FloatingChatWidget'
import ModuleRouteGuard from './components/ModuleRouteGuard'
import RoleBasedGuard from './components/RoleBasedGuard'

// Lazy Load Components
const Login = lazy(() => import('./components/Login'))
const PublicQuoteView = lazy(() => import('./components/PublicQuoteView'))
const PublicQuoteMicrosite = lazy(() => import('./components/Quotations/PublicQuoteView'))
const PublicPolicyView = lazy(() => import('./components/policies/PublicPolicyView'))
const PublicDocumentViewer = lazy(() => import('./components/PublicDocumentViewer'))
const PublicProcedureTracker = lazy(() => import('./components/Procedures/PublicProcedureTracker'))
const PublicTracker = lazy(() => import('./components/Claims/PublicTracker'))

const EmbedLayout = lazy(() => import('./layouts/EmbedLayout'))

const MobileLayout = lazy(() => import('./layouts/MobileLayout'))
const MobileHome = lazy(() => import('./components/Mobile/MobileHome'))
const MobileContacts = lazy(() => import('./components/Mobile/MobileContacts'))
const MobileQuotes = lazy(() => import('./components/Mobile/MobileQuotes'))
const MobileContactDetail = lazy(() => import('./components/Mobile/MobileContactDetail'))
const MobileProspects = lazy(() => import('./components/Mobile/MobileProspects'))
const MobileProspectDetail = lazy(() => import('./components/Mobile/MobileProspectDetail'))
const MobileNewProspect = lazy(() => import('./components/Mobile/MobileNewProspect'))
const MobileCollections = lazy(() => import('./components/Mobile/MobileCollections'))
const MobilePortfolio = lazy(() => import('./components/Mobile/MobilePortfolio'))
const MobilePolicyDetail = lazy(() => import('./components/Mobile/MobilePolicyDetail'))
const MobileProcedureDetail = lazy(() => import('./components/Mobile/MobileProcedureDetail'))
const MobileClaimDetail = lazy(() => import('./components/Mobile/MobileClaimDetail'))
const MobileNewPolicy = lazy(() => import('./components/Mobile/MobileNewPolicy'))
const MobileAgenda = lazy(() => import('./components/Mobile/MobileAgenda'))
const MobileComparator = lazy(() => import('./components/Mobile/MobileComparator'))
const MobileFinance = lazy(() => import('./components/Mobile/MobileFinance'))
const MobileMenu = lazy(() => import('./components/Mobile/MobileMenu'))
const MobileCRMDashboard = lazy(() => import('./components/Mobile/MobileCRMDashboard'))
const MobileRoadmap = lazy(() => import('./components/Mobile/MobileRoadmap'))
const MobileHelp = lazy(() => import('./components/Mobile/MobileHelp'))
const MobileReports = lazy(() => import('./components/Mobile/MobileReports'))
const MobileTasks = lazy(() => import('./components/Mobile/MobileTasks'))
const MobileMessages = lazy(() => import('./components/Mobile/MobileMessages'))
const MobileChatWindow = lazy(() => import('./components/Mobile/MobileChatWindow'))

const PublicWithChat = ({ children }) => {
    return (
        <>
            {children}
            <FloatingChatWidget mode="web" />
        </>
    )
}

const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            {/* Ruta Pública */}
            <Route
                path="/login"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando acceso..." />}>
                        <PublicWithChat>
                            <Login />
                        </PublicWithChat>
                    </Suspense>
                }
            />
            <Route
                path="/q/:id"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando cotización..." />}>
                        <PublicWithChat>
                            <PublicQuoteView />
                        </PublicWithChat>
                    </Suspense>
                }
            />
            <Route
                path="/p/:token"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando cotización..." />}>
                        <PublicWithChat>
                            <PublicQuoteMicrosite />
                        </PublicWithChat>
                    </Suspense>
                }
            />
            <Route
                path="/d/:token"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando documento..." />}>
                        <PublicWithChat>
                            <PublicDocumentViewer />
                        </PublicWithChat>
                    </Suspense>
                }
            />
            <Route
                path="/track-tramite/:token"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando estatus..." />}>
                        <PublicWithChat>
                            <PublicProcedureTracker />
                        </PublicWithChat>
                    </Suspense>
                }
            />
            <Route
                path="/track/:token"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando estatus..." />}>
                        <PublicWithChat>
                            <PublicTracker />
                        </PublicWithChat>
                    </Suspense>
                }
            />
            <Route
                path="/policy/:token"
                element={
                    <Suspense fallback={<LoadingScreen message="Cargando póliza..." />}>
                        <PublicWithChat>
                            <PublicPolicyView />
                        </PublicWithChat>
                    </Suspense>
                }
            />

            {/* Embed Chat Route */}
            <Route
                path="/embed-chat"
                element={
                    <Suspense fallback={<div className="h-screen w-screen bg-transparent" />}>
                        <EmbedLayout>
                            <FloatingChatWidget mode="web" embedded={true} isOpenDefault={false} />
                        </EmbedLayout>
                    </Suspense>
                }
            />

            {/* Mobile App */}
            <Route
                path="/"
                element={
                    <RoleBasedGuard>
                        <PrivateRoute>
                            <Suspense fallback={<LoadingScreen message="Cargando móvil..." />}>
                                <MobileLayout />
                            </Suspense>
                        </PrivateRoute>
                    </RoleBasedGuard>
                }
            >
                <Route path="home" element={<MobileHome />} />
                <Route path="tasks" element={<MobileTasks />} />
                <Route path="messages" element={<MobileMessages />} />
                <Route path="messages/:id" element={<MobileChatWindow />} />
                <Route path="contacts" element={<MobileContacts />} />
                <Route path="contacts/:id" element={<MobileContactDetail />} />
                <Route path="quotes" element={<MobileQuotes />} />
                <Route path="prospects" element={<MobileProspects />} />
                <Route path="prospects/:id" element={<MobileProspectDetail />} />
                <Route path="new-prospect" element={<MobileNewProspect />} />
                <Route path="collections" element={<MobileCollections />} />
                <Route path="portfolio" element={<MobilePortfolio />} />
                <Route path="policies/:id" element={<MobilePolicyDetail />} />
                <Route path="procedures/:id" element={<MobileProcedureDetail />} />
                <Route path="claims/:id" element={<MobileClaimDetail />} />
                <Route path="new-policy" element={<MobileNewPolicy />} />
                <Route path="agenda" element={<MobileAgenda />} />
                <Route path="menu" element={<MobileMenu />} />
                <Route
                    path="finance"
                    element={
                        <Suspense fallback={<LoadingScreen message="Cargando finanzas..." />}>
                            <MobileFinance />
                        </Suspense>
                    }
                />
                <Route
                    path="comparator"
                    element={
                        <ModuleRouteGuard module="comparator" action="view">
                            <MobileComparator />
                        </ModuleRouteGuard>
                    }
                />
                <Route path="crm" element={<MobileCRMDashboard />} />
                <Route path="roadmap" element={<MobileRoadmap />} />
                <Route path="reports" element={<MobileReports />} />
                <Route path="help" element={<MobileHelp />} />
                <Route index element={<Navigate to="home" replace />} />
            </Route>

            {/* Ruta 404 - Redirigir a inicio o login */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </>
    )
)

import { swrCacheProvider } from './lib/swr-cache'

const App = () => {
    return (
        <AuthProvider>
            <NotificationProvider>
                <SWRConfig
                    value={{
                        provider: swrCacheProvider,
                        revalidateOnFocus: false,
                        shouldRetryOnError: false
                    }}
                >
                    <ErrorBoundary>
                        <RouterProvider router={router} />
                    </ErrorBoundary>
                </SWRConfig>
            </NotificationProvider>
        </AuthProvider>
    )
}

export default App
